import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Plus, Briefcase, MapPin, Clock, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Layout from '@/components/Layout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY'
];

const STATUS_STYLES = {
  active: 'bg-green-100 text-green-700',
  inactive: 'bg-yellow-100 text-yellow-700',
  closed: 'bg-stone-200 text-stone-500',
};

const emptyForm = { title: '', description: '', city: '', state: 'NJ' };

export default function PostJobs({ tokenKey = 'token', useLayout = false }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [expandedJobs, setExpandedJobs] = useState({});

  useEffect(() => { fetchJobs(); }, []);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenKey)}` });

  const fetchJobs = async () => {
    try {
      const res = await axios.get(`${API}/jobs/my`, { headers: headers() });
      setJobs(res.data.jobs || []);
    } catch (e) {
      console.error('Failed to fetch jobs:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim() || !form.city.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/jobs`, form, { headers: headers() });
      toast.success('Career job posted!');
      setForm({ ...emptyForm });
      setShowDialog(false);
      fetchJobs();
    } catch (e) {
      toast.error('Failed to post job');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (jobId, newStatus) => {
    try {
      await axios.patch(`${API}/jobs/${jobId}/status`, { status: newStatus }, { headers: headers() });
      toast.success(`Job marked as ${newStatus}`);
      fetchJobs();
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm('Delete this career job?')) return;
    try {
      await axios.delete(`${API}/jobs/${jobId}`, { headers: headers() });
      toast.success('Job deleted');
      fetchJobs();
    } catch (e) {
      toast.error('Failed to delete job');
    }
  };

  const timeAgo = (d) => {
    if (!d) return '';
    const diff = Date.now() - new Date(d).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(diff / 3600000);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(diff / 86400000);
    if (days < 7) return `${days}d ago`;
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    const spinner = (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A4D2E]" />
      </div>
    );
    return useLayout ? <Layout>{spinner}</Layout> : spinner;
  }

  const content = (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1A4D2E] flex items-center gap-2" style={{ fontFamily: '"Playfair Display", serif' }}>
            <Briefcase size={28} /> Career Jobs
          </h1>
          <p className="text-stone-600 mt-1">Post and manage career opportunities</p>
        </div>
        <Button onClick={() => setShowDialog(true)} className="bg-[#1A4D2E] hover:bg-[#143D24]" data-testid="add-job-btn">
          <Plus size={18} className="mr-2" /> Add Job
        </Button>
      </div>

      {/* Created Jobs */}
      <div>
        <h2 className="text-lg font-semibold text-stone-900 mb-3">Created Jobs ({jobs.length})</h2>
        {jobs.length === 0 ? (
          <Card className="p-12 text-center">
            <Briefcase className="text-stone-300 mx-auto mb-3" size={36} />
            <p className="text-stone-500 mb-1">No jobs created yet</p>
            <p className="text-sm text-stone-400">Click "Add Job" to post your first career opportunity</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {jobs.map((job) => (
              <Card key={job.id} className="p-4 hover:shadow-md transition-shadow" data-testid={`job-card-${job.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="font-semibold text-stone-900">{job.title}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium capitalize ${STATUS_STYLES[job.status] || STATUS_STYLES.active}`} data-testid={`job-status-${job.id}`}>
                        {job.status}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-sm text-stone-500 mb-1">
                      <span className="flex items-center gap-1"><MapPin size={14} />{job.city}, {job.state}</span>
                      <span className="flex items-center gap-1"><Clock size={14} />{timeAgo(job.created_at)}</span>
                    </div>
                    <p className={`text-sm text-stone-600 ${expandedJobs[job.id] ? '' : 'line-clamp-2'}`}>{job.description}</p>
                    {job.description.length > 120 && (
                      <button onClick={() => setExpandedJobs(prev => ({ ...prev, [job.id]: !prev[job.id] }))} className="text-xs font-medium text-[#1A4D2E] hover:underline mt-0.5" data-testid={`toggle-desc-${job.id}`}>
                        {expandedJobs[job.id] ? 'Less' : 'More'}
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Select value={job.status} onValueChange={(v) => handleStatusChange(job.id, v)}>
                      <SelectTrigger className="h-8 text-xs w-[110px]" data-testid={`status-select-${job.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0" onClick={() => handleDelete(job.id)} data-testid={`delete-job-${job.id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add Job Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[500px]" data-testid="job-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-[#1A4D2E]">New Career Job</DialogTitle>
            <DialogDescription className="sr-only">Fill in job details</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div>
              <Label>Title *</Label>
              <Input placeholder="e.g. Office Admin" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="job-title-input" />
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea rows={4} placeholder="Describe the job..." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="job-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>City *</Label>
                <Input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="job-city-input" />
              </div>
              <div>
                <Label>State *</Label>
                <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
                  <SelectTrigger data-testid="job-state-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => { setShowDialog(false); setForm({ ...emptyForm }); }}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-[#1A4D2E] hover:bg-[#143D24]" data-testid="submit-job-btn">{saving ? 'Posting...' : 'Post Job'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );

  return useLayout ? <Layout>{content}</Layout> : content;
}
